import { AuxButton, AuxDatePicker, AuxSelect, AuxTooltip } from "@blk/aladdin-react-components-es";
import { AuxButtonSizeEnum, AuxButtonTypeEnum, AuxDatePickerDateFormatEnum, AuxDatePickerSeparatorsEnum } from "@blk/aladdin-web-components";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import "./insTenorSettings.scss";
import "components/time-period/insSettingsTimePeriod.scss";
import { regions } from "common/regions";
import { useResolvedCustomDateRangeEndDate, useResolvedCustomDateRangeStartDate, useResolvedTenor } from "components/widgets/hooks/use-current-widget";
import { useGlobalization } from "components/ux/ins-date-pickers";
import { useGetSelectedReferenceDate } from "components/workspaces/hooks/use-current-dashboard";
import { language } from "language/default";
import { setCurrentStatusCustomDateRangeEndDate, setCurrentStatusCustomDateRangeStartDate, setCurrentStatusTenor } from "store/current-dashboard";
import { TenorOptions } from "store/current-dashboard/types";
const getTenorDropdownOptions = (sectionId, widgetId) => {
    const tenorLang = language.en.skeleton.settingsMenu.tenorOptions;
    return [
        {
            values: [
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.oneMonth}`, displayValue: tenorLang.oneMonth, value: TenorOptions.oneMonth },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.sixMonths}`, displayValue: tenorLang.sixMonths, value: TenorOptions.sixMonths },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.oneQuarter}`, displayValue: tenorLang.oneQuarter, value: TenorOptions.oneQuarter },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.oneYear}`, displayValue: tenorLang.oneYear, value: TenorOptions.oneYear },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.twoYears}`, displayValue: tenorLang.twoYears, value: TenorOptions.twoYears },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.threeYears}`, displayValue: tenorLang.threeYears, value: TenorOptions.threeYears },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.fiveYears}`, displayValue: tenorLang.fiveYears, value: TenorOptions.fiveYears },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.tenYears}`, displayValue: tenorLang.tenYears, value: TenorOptions.tenYears },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.Itd}`, displayValue: tenorLang.Itd, value: TenorOptions.Itd },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.Ytd}`, displayValue: tenorLang.Ytd, value: TenorOptions.Ytd },
                { auxId: `${sectionId}-${widgetId}-${TenorOptions.customDateRange}`, displayValue: tenorLang.customDateRange, value: TenorOptions.customDateRange }
            ]
        }
    ];
};
const CalculationPeriodTooltip = () => React.createElement(AuxTooltip, null,
    React.createElement(AuxButton, { icon: "info-bold", size: AuxButtonSizeEnum.XSMALL, slot: "target", type: AuxButtonTypeEnum.TERTIARY_SUBTLE, className: "insSettingsTooltipIcon" }),
    React.createElement("div", { slot: "content" },
        React.createElement("div", { className: "insTooltipContentWrapper" },
            React.createElement("div", { className: "insTooltipTitle" }, language.en.skeleton.settingsMenu.calculationPeriod),
            React.createElement("div", null, language.en.dashboard.timePeriod.calculationPeriodTooltip))));
const getTenorSelectData = (tenor, sectionId, widgetId) => [{
        values: getTenorDropdownOptions(sectionId, widgetId)[0].values.map((option) => ({
            ...option,
            isSelected: option.value === tenor
        }))
    }];
const getDateParts = (date) => ({
    dd: String(date.getDate()).padStart(2, "0"),
    mm: String(date.getMonth() + 1).padStart(2, "0"),
    yyyy: String(date.getFullYear())
});
const formatDateByFormat = (dd, mm, yyyy, dateFormat, dateSeparator) => {
    const formatMap = {
        [AuxDatePickerDateFormatEnum.DD_MM_YYYY]: `${dd}${dateSeparator}${mm}${dateSeparator}${yyyy}`,
        [AuxDatePickerDateFormatEnum.YYYY_MM_DD]: `${yyyy}${dateSeparator}${mm}${dateSeparator}${dd}`,
        [AuxDatePickerDateFormatEnum.MM_DD_YYYY]: `${mm}${dateSeparator}${dd}${dateSeparator}${yyyy}`
    };
    return formatMap[dateFormat] ?? `${mm}${dateSeparator}${dd}${dateSeparator}${yyyy}`;
};
const formatDateForPicker = (date, dateFormat, dateSeparator) => {
    if (!date) {
        return undefined;
    }
    const { dd, mm, yyyy } = getDateParts(date);
    return formatDateByFormat(dd, mm, yyyy, dateFormat, dateSeparator);
};
const isValidTenorSelection = (selectedTenor) => Boolean(selectedTenor && Object.values(TenorOptions).includes(selectedTenor));
const shouldPendCustomDateRange = (typedTenor, startDate, endDate) => typedTenor === TenorOptions.customDateRange && !(startDate && endDate);
const handleTenorSelection = (dispatch, endDate, sectionId, setPendingTenor, startDate, typedTenor, widgetId) => {
    if (shouldPendCustomDateRange(typedTenor, startDate, endDate)) {
        setPendingTenor(TenorOptions.customDateRange);
    }
    else {
        setPendingTenor(undefined);
        dispatch(setCurrentStatusTenor({ sectionId, selectedTenor: typedTenor, widgetId }));
    }
};
const isValidDateObject = (dateObject) => Boolean(dateObject && !isNaN(dateObject.getTime()));
const handleEndDateWithPending = (dispatch, pendingTenor, sectionId, setPendingTenor, widgetId) => {
    if (pendingTenor === TenorOptions.customDateRange) {
        dispatch(setCurrentStatusTenor({ sectionId, selectedTenor: TenorOptions.customDateRange, widgetId }));
        setPendingTenor(undefined);
    }
};
const useTenorEventHandlers = (dispatch, endDate, pendingTenor, sectionId, setPendingTenor, startDate, widgetId) => {
    const handleSelectionChanged = useCallback((event) => {
        const rawValue = event.detail.value;
        const selectedTenor = typeof rawValue === "string" ? rawValue : rawValue?.value;
        if (!isValidTenorSelection(selectedTenor)) {
            return;
        }
        const typedTenor = selectedTenor;
        handleTenorSelection(dispatch, endDate, sectionId, setPendingTenor, startDate, typedTenor, widgetId);
    }, [dispatch, endDate, sectionId, setPendingTenor, startDate, widgetId]);
    const onStartDateChange = useCallback((event) => {
        const dateObject = event.detail?.dateObject;
        if (isValidDateObject(dateObject)) {
            dispatch(setCurrentStatusCustomDateRangeStartDate({ sectionId, startDate: dateObject, widgetId }));
        }
    }, [dispatch, sectionId, widgetId]);
    const onEndDateChange = useCallback((event) => {
        const dateObject = event.detail?.dateObject;
        if (isValidDateObject(dateObject)) {
            dispatch(setCurrentStatusCustomDateRangeEndDate({ endDate: dateObject, sectionId, widgetId }));
            handleEndDateWithPending(dispatch, pendingTenor, sectionId, setPendingTenor, widgetId);
        }
    }, [dispatch, pendingTenor, sectionId, setPendingTenor, widgetId]);
    return { handleSelectionChanged, onEndDateChange, onStartDateChange };
};
// eslint-disable-next-line max-statements
export const InsTenorSettings = ({ widgetId, sectionId }) => {
    const dispatch = useDispatch();
    const tenor = useResolvedTenor(sectionId, widgetId) || TenorOptions.Itd;
    const startDate = useResolvedCustomDateRangeStartDate(sectionId, widgetId);
    const endDate = useResolvedCustomDateRangeEndDate(sectionId, widgetId);
    const referenceDate = useGetSelectedReferenceDate();
    const asOfDate = useMemo(() => referenceDate ?? new Date(), [referenceDate]);
    const { region } = useGlobalization();
    const dateFormat = regions[region]?.datePickerDateFormat ?? AuxDatePickerDateFormatEnum.MM_DD_YYYY;
    const dateSeparator = AuxDatePickerSeparatorsEnum.FORWARD_SLASH;
    const [pendingTenor, setPendingTenor] = useState(undefined);
    const effectiveTenor = pendingTenor ?? tenor;
    const { handleSelectionChanged, onEndDateChange, onStartDateChange } = useTenorEventHandlers(dispatch, endDate, pendingTenor, sectionId, setPendingTenor, startDate, widgetId);
    const isCustomDateRange = effectiveTenor === TenorOptions.customDateRange;
    const selectData = useMemo(() => getTenorSelectData(effectiveTenor, sectionId, widgetId), [sectionId, effectiveTenor, widgetId]);
    return React.createElement("div", null,
        React.createElement("div", { className: "insRadioGroupDiv" },
            React.createElement("span", { className: "insSettingsLabel" }, language.en.skeleton.settingsMenu.calculationPeriod),
            React.createElement(CalculationPeriodTooltip, null)),
        React.createElement("div", { className: "insRadioGroupDiv" },
            React.createElement(AuxSelect, { onSelectionChanged: handleSelectionChanged, hasInitialOptionPlaceholder: false, id: "tenorSelect", className: "insRadioSelectWidth", isCompact: false, size: AuxButtonSizeEnum.SMALL, data: selectData })),
        isCustomDateRange &&
            React.createElement("div", { className: "custom-date-range-tenor" },
                React.createElement(AuxDatePicker, { closeCalendarOnBlur: false, dateFormat: dateFormat, displayDateFormat: dateFormat, maxDate: formatDateForPicker(endDate ?? asOfDate, dateFormat, dateSeparator), onValueChanged: onStartDateChange, separator: dateSeparator, size: "small", value: formatDateForPicker(startDate, dateFormat, dateSeparator) }),
                React.createElement("span", { className: "date-separator" }, "-"),
                React.createElement(AuxDatePicker, { closeCalendarOnBlur: false, dateFormat: dateFormat, displayDateFormat: dateFormat, isDisabled: !startDate, maxDate: formatDateForPicker(asOfDate, dateFormat, dateSeparator), minDate: formatDateForPicker(startDate, dateFormat, dateSeparator), onValueChanged: onEndDateChange, separator: dateSeparator, size: "small", value: formatDateForPicker(endDate, dateFormat, dateSeparator) })));
};
